import { useQuery } from '@tanstack/react-query'
import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios'
import type { UseQueryOptionsWithoutQueryKey } from '../../react-query-config'
import type { ResponseApiError } from '../../models/axios-error'
import { mapWeeklyGmvEvolution, type WeeklyGmvEvolution, type WeeklyGmvEvolutionResponse } from './weekly-gmv'
import type { Channel } from './home.model'

const QUERY_KEY_LAST_FOUR_WEEKS_STORE_PERFORMANCE = 'last_four_weeks_store_performance'

export interface LastFourWeeksStorePerformanceRequest {
  channel: Channel
  storeId: number
}

export interface YearComparison {
  current: number
  previous: number
}

interface LastFourWeeksStorePerformanceApiResponse {
  from?: string | null
  to?: string | null
  conversion_rate?: YearComparison | null
  store_traffic?: YearComparison | null
  basket_average_value?: YearComparison | null
  basket_average_quantity?: YearComparison | null
  weekly_gmv?: WeeklyGmvEvolutionResponse | null
}

export interface LastFourWeeksStorePerformance {
  from: Date
  to: Date
  conversionRate: YearComparison
  storeTraffic: YearComparison
  basketAverageValue: YearComparison
  basketAverageQuantity: YearComparison
  weeklyGmv: WeeklyGmvEvolution
}

const toLastFourWeeksStorePerformance = (
  response: LastFourWeeksStorePerformanceApiResponse
): LastFourWeeksStorePerformance => ({
  from: (response.from && new Date(response.from)) || null,
  to: (response.to && new Date(response.to)) || null,
  conversionRate: response.conversion_rate,
  storeTraffic: response.store_traffic,
  basketAverageValue: response.basket_average_value,
  basketAverageQuantity: response.basket_average_quantity,
  weeklyGmv: mapWeeklyGmvEvolution(response.weekly_gmv)
})

const getLastFourWeeksStorePerformance = async (config: AxiosRequestConfig): Promise<LastFourWeeksStorePerformance> => {
  const axiosPromise: AxiosResponse<LastFourWeeksStorePerformanceApiResponse> = await axios(config)
  return toLastFourWeeksStorePerformance(axiosPromise.data)
}

export const useApiGetLastFourWeeksStorePerformance = (
  args: LastFourWeeksStorePerformanceRequest,
  enabled: boolean,
  options?: UseQueryOptionsWithoutQueryKey<LastFourWeeksStorePerformance>
) => {
  const { storeId, channel } = args
  const url = `/store-last-four-weeks-performance?channel=${channel}&storeId=${storeId}`
  const config: AxiosRequestConfig = { method: 'get', url }
  return useQuery<LastFourWeeksStorePerformance, ResponseApiError>({
    queryKey: [QUERY_KEY_LAST_FOUR_WEEKS_STORE_PERFORMANCE, url],
    queryFn: () => getLastFourWeeksStorePerformance(config),
    enabled,
    ...options
  })
}
